import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography, Button, IconButton, TableCell, TableRow, TableHead, TableBody, TableContainer, Paper } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate } from 'react-router-dom';
import APIService from '../../Services/APIService.js';
import { dateFormate, generateFilterKey } from '../../utils/helper.jsx';
import { userInformationAtom } from '../../Services/_states/index.js';
import { useRecoilValue } from 'recoil';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import PermissionModal from '../Modals/PermissionModal.jsx';
import SweetAlert from '../Shared/Notification/index.jsx';
import SearchBox from '../FlowBuilder/Common/SearchBox/index.tsx';
import Pagination from '../FlowBuilder/Common/Pagination/index.tsx';

const LeadSettings = () => {
  const navigate = useNavigate();
  const user = useRecoilValue(userInformationAtom);
  const [leads, setLeads] = useState([]);
  const [leadsFormData, setLeadsFormData] = useState([]);
  const [state, setState] = useState({
    showPermissionModal: false,
    modalProfileId: undefined,
    createPermission: false,
    existingProfileIds: [],
    dataInfo: [],
    profilePermissionId: undefined,
  });

  const breadcrumbsData = [
    { label: <IconButton color='primary'><HomeIcon /></IconButton>, href: "/"  },
    { label: 'Lead Permissions' },
  ];

  const handleEditProfile = (profileId) => {
    setState(prev => ({
      ...prev,
      showPermissionModal: true,
      modalProfileId: profileId,
      createPermission: false,
    }));
  };

  const handleDeleteProfile = async (permissionId) => {
    const res = await APIService.remove(`/v0/ui/records/permissions?permission_id=${permissionId}`);
    if (res.status === 200) SweetAlert('Profile Lead Permission deleted successfully', 'success');
  };

  const getAllLeadsFormData = async () => {
    const res = await APIService.get(`/v0/ui/records/list_all_leads_forms_by_campaign/`);
    if (res.status === 200) {
      setLeadsFormData(res.data);
      const profilePermission = res.data.map(campaign => ({
        entityName: campaign.campaign_name,
        entityId: campaign.campaign_id,
        type: 'campaign',
        permission: 'None',
        data: campaign.leads_forms.map(lead_form => ({
          entityName: lead_form.lead_form_name,
          entityId: lead_form.lead_form_id,
          type: 'lead_form',
          permission: 'None'
        }))
      }));
      return profilePermission;
    }
  };

  const openCreatePermissionModal = async () => {
    await getAllLeadsFormData();
    const existingProfileIds = leads.map(p => p.profile_id.id);
    setState(prev => ({
      ...prev,
      existingProfileIds,
      showPermissionModal: true,
      createPermission: true
    }));
  };

  const onModalSubmit = async (stateData) => {
    const requestData = {
      id: state.createPermission ? undefined : state.profilePermissionId,
      profile_id: state.createPermission ? stateData.selectedProfile.value : state.modalProfileId,
      leads_permissions: { campaigns: {}, leads_forms: {} },
    };

    stateData.data.data.forEach(campaignData => {
      if (campaignData.permission !== 'None') {
        requestData.leads_permissions.campaigns[campaignData.entityId] = campaignData.permission === 'Edit'
          ? ['EDIT', 'VIEW', 'DELETE', 'FILL', 'FREEZE', 'UNFREEZE']
          : ['VIEW', 'FILL', 'FREEZE'];
      }
      campaignData.data.forEach(leadFormData => {
        if (leadFormData.permission !== 'None') {
          requestData.leads_permissions.leads_forms[leadFormData.entityId] = leadFormData.permission === 'Edit'
            ? ['EDIT', 'VIEW', 'DELETE', 'FILL']
            : ['VIEW', 'FILL'];
        }
      });
    });

    setState(prev => ({
      ...prev,
      showPermissionModal: false,
      modalProfileId: undefined,
      createPermission: false,
    }));

    const apiCall = state.createPermission ? APIService.post : APIService.put;
    const res = await apiCall(`/v0/ui/records/permissions/`, [requestData]);
    if (res.status === 200) SweetAlert(`Profile Lead Permission ${state.createPermission ? 'created' : 'updated'} successfully`, 'success');
  };

  const closePermissionModal = () => {
    setState(prev => ({
      ...prev,
      showPermissionModal: false,
      createPermission: false,
    }));
  };

  const getLeadPermissionList = async () => {
    const res = await APIService.get(`/v0/ui/records/permissions/`);
    if (res.status === 200) setLeads(res.data);
  };

  useEffect(() => {
    getLeadPermissionList();
  }, [state.showPermissionModal]);

  const columns = useMemo(() => [
    {
      accessorKey: 'index',
      header: 'Index',
      Cell: ({ row }) => row.index + 1
    },
    {
      accessorKey: 'profile_id.name',
      header: 'Name',
      Cell: ({ row }) => row.original.profile_id.name
    },
    {
      accessorKey: 'permission',
      header: 'Permission',
      Cell: () => 'Custom'
    },
    {
      accessorKey: 'created_by',
      header: 'Created by',
      Cell: ({ row }) => `${row.original.created_by.username} ${row.original.created_by.last_name}`
    },
    {
      accessorKey: 'action',
      header: 'Actions',
      Cell: ({ row }) => (
        <Box>
          <IconButton onClick={() => handleEditProfile(row.original.profile_id.id)}>
            <MoreVertIcon />
          </IconButton>
          <IconButton onClick={() => handleDeleteProfile(row.original.id)}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      )
    }
  ], [leads]);

  return (
    <Box sx={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
      <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={(event, item) => item?.onClick?.()} />

      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h4" fontWeight="bold">Lead Permissions</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" onClick={openCreatePermissionModal}>Create</Button>
        </Grid>
      </Grid>

      <CommonTable
        columns={columns}
        data={leads || []}
        muiTableContainerProps={{
          sx: {
            height: 'calc(100vh - 300px)',
            width: '100%',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px'
            }
          }
        }}
        state={{ isLoading: false }}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              totalCount={leads.length}
              rowsPerPage={10}
              page={0}
              handleChangePage={() => { }}
              handleChangeRowsPerPage={() => { }}
            />
          </Box>
        )}
      />

      {state.showPermissionModal && (
        <PermissionModal {...state} onClose={closePermissionModal} onSubmit={onModalSubmit} />
      )}
    </Box>
  );
};

export default LeadSettings;